import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CHART_COLORS } from '../../../theme/brandColors';
import Avatar from '../../atoms/Avatar';
import { AvatarSize } from '../../atoms/Avatar/enums';
import { MultiSelectValue } from '../../atoms/Fields/MultiSelect/types';
import { RadioItemWithCheckboxesProps } from '../../atoms/Fields/Radio/content/withCheckboxes/types';
import {
  IconChevronDown,
  IconChevronRight,
} from '../../../assets/svg/icons';
import {
  StyledWSActiveBadge,
  StyledWSEntityCard,
  StyledWSEntityDot,
  StyledWSEntityGrid,
  StyledWSEntityName,
  StyledWSLeftPane,
  StyledWSLeftPaneTitle,
  StyledWSPanel,
  StyledWSRightHeader,
  StyledWSRightPane,
  StyledWSRightTitle,
  StyledWSWorkspaceAvatar,
  StyledWSWorkspaceChevron,
  StyledWSWorkspaceCount,
  StyledWSWorkspaceInfo,
  StyledWSWorkspaceItem,
  StyledWSWorkspaceName,
  StyledWSTrigger,
  StyledWSTriggerChevron,
  StyledWSTriggerName,
  StyledWSWrapper,
} from './styles';

const AVATAR_COLORS = [...CHART_COLORS];

const getAvatarColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitial = (name: string): string => {
  const clean = name.replace(/[^a-zA-Z؀-ۿ]/g, ' ').trim();
  return clean ? clean[0].toUpperCase() : '?';
};

interface WorkspaceSwitcherProps {
  items: RadioItemWithCheckboxesProps[];
  value?: MultiSelectValue;
  onChange: (item: RadioItemWithCheckboxesProps) => void;
  collapsed?: boolean;
  userLogo?: string;
  userName?: string;
  storeName?: string;
  disabled?: boolean;
  onDisabledClick?: () => void;
}

const WorkspaceSwitcher: React.FC<WorkspaceSwitcherProps> = ({
  items,
  value,
  onChange,
  collapsed,
  userLogo,
  userName,
  storeName,
  disabled,
  onDisabledClick,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | undefined>();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setHoveredId(undefined);
  }, [isOpen]);

  const previewId = hoveredId ?? value?.value;
  const previewWorkspace = useMemo(
    () => items.find((i) => i.value === previewId),
    [items, previewId]
  );

  const displayName = storeName || userName || '';

  const handleTriggerClick = () => {
    if (disabled) {
      onDisabledClick?.();
      return;
    }
    setIsOpen((o) => !o);
  };

  const handleWorkspaceClick = useCallback(
    (item: RadioItemWithCheckboxesProps) => {
      onChange(item);
      setIsOpen(false);
    },
    [onChange]
  );

  return (
    <StyledWSWrapper ref={containerRef}>
      <StyledWSTrigger
        type='button'
        onClick={handleTriggerClick}
        $collapsed={collapsed}
      >
        <Avatar size={AvatarSize.Small} src={userLogo} alt={displayName} />
        {!collapsed && (
          <>
            <StyledWSTriggerName>{displayName}</StyledWSTriggerName>
            {!disabled && (
              <StyledWSTriggerChevron isOpen={isOpen}>
                <IconChevronDown />
              </StyledWSTriggerChevron>
            )}
          </>
        )}
      </StyledWSTrigger>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            key='ws-panel'
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15, type: 'tween' }}
            style={{ position: 'absolute', top: '100%', left: 0, width: '100%', zIndex: 400 }}
          >
            <StyledWSPanel>
              <StyledWSLeftPane>
                <StyledWSLeftPaneTitle>Workspaces</StyledWSLeftPaneTitle>
                {items.map((item) => {
                  const isSelected = item.value === value?.value;
                  const label =
                    typeof item.label === 'string' ? item.label : '';
                  const color = getAvatarColor(label);
                  const initial = getInitial(label);
                  return (
                    <StyledWSWorkspaceItem
                      key={item.value}
                      isSelected={isSelected}
                      type='button'
                      onMouseEnter={() => setHoveredId(item.value)}
                      onMouseLeave={() => setHoveredId(undefined)}
                      onClick={() => handleWorkspaceClick(item)}
                    >
                      <StyledWSWorkspaceAvatar
                        color={color}
                        hasLogo={!!item.decor}
                      >
                        {item.decor ? (
                          <img src={item.decor} alt={label} />
                        ) : (
                          <span>{initial}</span>
                        )}
                      </StyledWSWorkspaceAvatar>
                      <StyledWSWorkspaceInfo>
                        <StyledWSWorkspaceName>{label}</StyledWSWorkspaceName>
                        <StyledWSWorkspaceCount>
                          {item.checkboxes.length}{' '}
                          {item.checkboxes.length === 1 ? 'entity' : 'entities'}
                        </StyledWSWorkspaceCount>
                      </StyledWSWorkspaceInfo>
                      {isSelected && (
                        <StyledWSWorkspaceChevron>
                          <IconChevronRight />
                        </StyledWSWorkspaceChevron>
                      )}
                    </StyledWSWorkspaceItem>
                  );
                })}
              </StyledWSLeftPane>

              {previewWorkspace && (
                <StyledWSRightPane>
                  <StyledWSRightHeader>
                    <StyledWSRightTitle>
                      {typeof previewWorkspace.label === 'string'
                        ? previewWorkspace.label
                        : ''}
                    </StyledWSRightTitle>
                    {previewWorkspace.value === value?.value && (
                      <StyledWSActiveBadge>Active</StyledWSActiveBadge>
                    )}
                  </StyledWSRightHeader>

                  <StyledWSEntityGrid>
                    {previewWorkspace.checkboxes.map((entity) => {
                      const isChecked =
                        previewWorkspace.value === value?.value &&
                        (value?.checkedSubItems ?? []).includes(entity.value);
                      return (
                        <StyledWSEntityCard
                          key={entity.value}
                          type='button'
                          isChecked={isChecked}
                          onClick={(e) => {
                            e.stopPropagation();
                            entity.onChange?.(e as any);
                          }}
                        >
                          <StyledWSEntityDot isChecked={isChecked} />
                          <StyledWSEntityName isChecked={isChecked}>
                            {entity.label}
                          </StyledWSEntityName>
                        </StyledWSEntityCard>
                      );
                    })}
                  </StyledWSEntityGrid>
                </StyledWSRightPane>
              )}
            </StyledWSPanel>
          </motion.div>
        )}
      </AnimatePresence>
    </StyledWSWrapper>
  );
};

export default WorkspaceSwitcher;
